import { GID_MASK, FLIPPED_HORIZONTALLY_FLAG, FLIPPED_VERTICALLY_FLAG, FLIPPED_DIAGONALLY_FLAG } from '../utils/constants.js';

/**
 * Off-screen buffer rendering for the tile map.
 * Renders tile layers to off-screen canvases for compositing.
 * Supports tile animations via tileAnimations state.
 */
export class MapCanvas {
  constructor(state) {
    this.state = state;
    this._layerCanvases = new Map(); // layerIndex -> { canvas, ctx, dirty }
    this._animTime = 0;
    this._lastFrameTime = 0;
    this._animating = false;
    this._lastAnimFrames = new Map(); // tileId → last resolved frame tileId
  }

  /** Start animation loop if there are animated tiles. */
  startAnimationLoop(compositor) {
    if (this._animating) return;
    this._animating = true;
    this._lastFrameTime = performance.now();

    const tick = (time) => {
      if (!this._animating) return;
      const dt = time - this._lastFrameTime;
      this._lastFrameTime = time;
      this._animTime += dt;

      const tileAnimations = this.state.get('tileAnimations');
      if (tileAnimations && tileAnimations.size > 0) {
        // Only invalidate if any animated tile's frame actually changed
        if (this._hasAnimFrameChanged(tileAnimations)) {
          this.invalidateAll();
          compositor.requestRender();
        }
      }

      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /** Check if any animated tile's current frame differs from last render. */
  _hasAnimFrameChanged(tileAnimations) {
    let changed = false;
    for (const [tileId, frames] of tileAnimations) {
      let totalDuration = 0;
      for (const f of frames) totalDuration += f.duration;
      if (totalDuration === 0) continue;

      let elapsed = this._animTime % totalDuration;
      let currentFrame = frames[0].tileId;
      for (const frame of frames) {
        elapsed -= frame.duration;
        if (elapsed < 0) { currentFrame = frame.tileId; break; }
      }

      const lastFrame = this._lastAnimFrames.get(tileId);
      if (lastFrame !== currentFrame) {
        this._lastAnimFrames.set(tileId, currentFrame);
        changed = true;
      }
    }
    return changed;
  }

  /** Resolve the current display GID for an animated tile. */
  _resolveAnimatedGid(gid) {
    const tileAnimations = this.state.get('tileAnimations');
    if (!tileAnimations || tileAnimations.size === 0) return gid;

    const tileId = gid & GID_MASK;
    const flags = gid & ~GID_MASK;
    const frames = tileAnimations.get(tileId);
    if (!frames || frames.length === 0) return gid;

    // Calculate total animation duration
    let totalDuration = 0;
    for (const f of frames) totalDuration += f.duration;
    if (totalDuration === 0) return gid;

    // Find current frame
    let elapsed = this._animTime % totalDuration;
    for (const frame of frames) {
      elapsed -= frame.duration;
      if (elapsed < 0) {
        // Get the tileset for this tile to convert local ID to global
        const ts = this.state.getTilesetForGid(tileId);
        if (!ts) return gid;
        return (ts.firstGid + frame.tileId) | flags;
      }
    }
    return gid;
  }

  /** Render a single tile at (x, y) on the given layer canvas. */
  renderTile(ctx, gid, px, py, tileWidth, tileHeight) {
    if (gid <= 0) return;

    // Resolve animation
    gid = this._resolveAnimatedGid(gid);

    // Extract flip flags
    const flippedH = (gid & FLIPPED_HORIZONTALLY_FLAG) !== 0;
    const flippedV = (gid & FLIPPED_VERTICALLY_FLAG) !== 0;
    const flippedD = (gid & FLIPPED_DIAGONALLY_FLAG) !== 0;
    const tileId = gid & GID_MASK;

    const rect = this.state.getTileRect(tileId);
    if (!rect) return;

    const img = this.state.get('tilesetImages').get(rect.tileset.image);
    if (!img) return;

    ctx.save();
    ctx.translate(px + tileWidth / 2, py + tileHeight / 2);

    if (flippedD) {
      // Diagonal flip = rotate 90 CCW + flip horizontally
      ctx.transform(0, 1, 1, 0, 0, 0);
    }
    if (flippedH) ctx.scale(-1, 1);
    if (flippedV) ctx.scale(1, -1);

    ctx.drawImage(
      img,
      rect.sx, rect.sy, rect.sw, rect.sh,
      -tileWidth / 2, -tileHeight / 2, tileWidth, tileHeight
    );
    ctx.restore();
  }

  /**
   * Render only the visible portion of a tile layer.
   * Returns { canvas, originX, originY } where origin is the world-pixel
   * position of the canvas top-left corner.
   *
   * @param {number} layerIndex
   * @param {Object} bounds — { minTileX, minTileY, maxTileX, maxTileY }
   */
  renderViewport(layerIndex, bounds) {
    const state = this.state;
    const layer = state.get('layers')[layerIndex];
    if (!layer || layer.type !== 'tilelayer') return null;

    const mapW = state.get('mapWidth');
    const mapH = state.get('mapHeight');
    const tileW = state.get('tileWidth');
    const tileH = state.get('tileHeight');

    // Clamp bounds to map extents
    const minX = Math.max(0, bounds.minTileX);
    const minY = Math.max(0, bounds.minTileY);
    const maxX = Math.min(mapW - 1, bounds.maxTileX);
    const maxY = Math.min(mapH - 1, bounds.maxTileY);

    const tilesW = maxX - minX + 1;
    const tilesH = maxY - minY + 1;
    if (tilesW <= 0 || tilesH <= 0) return null;

    const neededW = tilesW * tileW;
    const neededH = tilesH * tileH;

    // Use numeric layerIndex as cache key (must match invalidateLayer/invalidateAll)
    let entry = this._layerCanvases.get(layerIndex);
    if (!entry) {
      const canvas = document.createElement('canvas');
      entry = { canvas, ctx: canvas.getContext('2d'), dirty: true, minX: -1, minY: -1, maxX: -1, maxY: -1 };
      this._layerCanvases.set(layerIndex, entry);
    }

    // Check if bounds or size changed
    const boundsChanged = entry.minX !== minX || entry.minY !== minY || entry.maxX !== maxX || entry.maxY !== maxY;
    if (entry.canvas.width !== neededW || entry.canvas.height !== neededH) {
      entry.canvas.width = neededW;
      entry.canvas.height = neededH;
      entry.dirty = true;
    } else if (boundsChanged) {
      entry.dirty = true;
    }

    if (!entry.dirty) {
      return { canvas: entry.canvas, originX: minX * tileW, originY: minY * tileH };
    }

    entry.ctx.clearRect(0, 0, neededW, neededH);

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const gid = layer.data[y * mapW + x];
        if (gid > 0) {
          this.renderTile(entry.ctx, gid, (x - minX) * tileW, (y - minY) * tileH, tileW, tileH);
        }
      }
    }

    entry.dirty = false;
    entry.minX = minX;
    entry.minY = minY;
    entry.maxX = maxX;
    entry.maxY = maxY;
    return { canvas: entry.canvas, originX: minX * tileW, originY: minY * tileH };
  }

  /** Render an entire tile layer to an off-screen canvas. Legacy fallback. */
  renderFullLayer(layerIndex) {
    const mapW = this.state.get('mapWidth');
    const mapH = this.state.get('mapHeight');
    const result = this.renderViewport(layerIndex, { minTileX: 0, minTileY: 0, maxTileX: mapW - 1, maxTileY: mapH - 1 });
    return result?.canvas || null;
  }

  /** Mark a layer as needing re-render. */
  invalidateLayer(layerIndex) {
    const entry = this._layerCanvases.get(layerIndex);
    if (entry) entry.dirty = true;
  }

  /** Mark all layers dirty. */
  invalidateAll() {
    for (const entry of this._layerCanvases.values()) {
      entry.dirty = true;
    }
  }

  /** Clear all cached canvases. */
  clear() {
    this._layerCanvases.clear();
  }
}
